import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import Papa from "papaparse";
import { Box, Typography, Stack, List } from "@mui/material";
import FileDropzone from "components/inputs/FileDropzone";
import { useTranslation } from "react-i18next";
import { TOAST_TYPE, ToastContext } from "context/toastContext";
import ImportAlert, { ImportAlertProps } from "components/importer/ImportAlert";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { Button } from "@mui/material";
import useAlerts from "./useImportAlerts";
import { AppContext } from "context/appContext";
import { SavedImportMapping, WIZARD_ACTION } from "types";
import AuturaDropdown from "components/inputs/AuturaDropdown";
import { ScrollableFormContainer } from "components/layout/ScrollableContainer";
import { logError } from "utils";
import { ImportWizardContext } from "components/importer/ImportWizardContext";

export interface UploadStepProps {
  rightColumn?: ReactNode;
}

const UploadStep = <F extends string>({
  rightColumn,
}: UploadStepProps): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch, fieldMetadata } = useContext(ImportWizardContext);
  const { state: appState } = useContext(AppContext);
  const ALERTS = useAlerts({
    discardedRows: state.discardedRows.length,
    successfulImports: state.csvData.length - state.discardedRows.length,
    entityName: state.entityName,
  });

  const displayToast = useContext(ToastContext);
  const [savedMapping, setSavedMapping] = useState<SavedImportMapping | null>(
    null,
  );
  const [dropzoneState, setDropzoneState] = useState<
    "ready" | "loading" | "completed"
  >("ready");
  const [centerAlerts, setCenterAlerts] = useState<ImportAlertProps[]>([
    ALERTS.WELCOME,
  ]);

  // file zone handlers
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setDropzoneState("loading");
    const file = acceptedFiles[0];
    if (file) {
      Papa.parse(file, {
        quoteChar: '"',
        escapeChar: '"',
        skipEmptyLines: "greedy",
        header: true,
        complete: (results: Papa.ParseResult<Record<string, string>>) => {
          dispatch({
            type: WIZARD_ACTION.IMPORT_CSV_DATA,
            payload: {
              fileName: file.name,
              results: results,
            },
          });

          //  set alerts
          if (results.errors.length > 0) {
            setCenterAlerts(generateErrorAlertProps(results.errors));
          } else if (state.csvUsesTemplate) {
            setCenterAlerts([ALERTS.TEMPLATE_RECOGNIZED]);
          } else {
            setCenterAlerts([ALERTS.PERFECT_PARSE]);
          }

          setDropzoneState("completed");
        },
        error: (error) => {
          displayToast({
            message: t("error.parse", {
              name: "CSV",
            }),
            type: TOAST_TYPE.ERROR,
          });
          logError(error);
          setDropzoneState("ready");
        },
      });
    } else {
      displayToast({
        message: t("file.dropzoneUnsupported"),
        type: TOAST_TYPE.ERROR,
      });
      setDropzoneState("ready");
    }
  }, []);
  const onClearFile = () => {
    dispatch({ type: WIZARD_ACTION.RESET_STATE });
    setDropzoneState("ready");
  };
  const generateErrorAlertProps = (
    errors: Papa.ParseError[],
  ): ImportAlertProps[] => {
    const errorAlerts = [ALERTS.CSV_IMPORT_TIPS];
    for (const error of errors) {
      const { code, message, row } = error;
      // Use a regular expression to add spaces before capital letters
      const title = code.replace(/(?<!^)([A-Z])/g, " $1");
      const paragraphs = [];
      if (row) {
        paragraphs.push(`${message} ${t("row")} ${row + 1}`);
      } else {
        paragraphs.push(message);
      }
      errorAlerts.push({
        title: title,
        paragraphs: paragraphs,
        severity: "error",
        sx: { mb: 2 },
      });
    }
    return errorAlerts;
  };

  // // event handlers
  const handleSelectSavedMapping = (e: ChangeEvent<HTMLInputElement>) => {
    const savedMapping = appState.tenant?.data?.savedImportMappings?.find(
      (pt) => pt.name === e.target.value,
    );
    if (savedMapping) {
      setSavedMapping(savedMapping);
      dispatch({
        type: WIZARD_ACTION.IMPORT_SAVED_MAPPINGS,
        payload: savedMapping.mappings,
      });
    }
  };
  const handleDownloadCSVTemplate = () => {
    // create a csv template with the entity fields and their helper text
    const csvContent = Papa.unparse({
      fields: state.entityFields.map((header: F) => t(header) as string),
      data: [
        state.entityFields.map((field: F, index: number) => {
          const metadata = fieldMetadata[field];
          const alerts = metadata.alerts.map((alert) => {
            if (alert.title === "Required Field") {
              return "This field must contain a value";
            }
            if (alert.title === "Optional Field") {
              return "This is an optional field and can be left blank if not applicable";
            }
            return alert.paragraphs.join(". ").replace(/,\s*/g, "");
          });
          if (index === 0) {
            return `** DO NOT EDIT THIS ROW ** ${alerts.join(". ")}`;
          }
          return alerts.join(". ");
        }),
      ],
    });

    // download the csv template
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${state.entityName}_import_template.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Stack direction="row" spacing={4} display="flex">
      <ScrollableFormContainer maxHeight={`calc(100dvh - 326px)`}>
        <Stack direction="row" gap={6} display="flex" pt={2}>
          <Stack direction="column" spacing={4} flex={2}>
            <FileDropzone
              onDrop={onDrop}
              accept={{
                "text/csv": [".csv"],
              }}
              state={dropzoneState}
              onCancel={onClearFile}
              completedLabel={state.fileName}
              loadingLabel={"Applying magic..."}
              caption={t("uploadCSVHelper", { ns: "importWizard" })}
            />
            <Box>
              <Button
                variant="contained"
                startIcon={<CloudDownloadIcon />}
                onClick={handleDownloadCSVTemplate}
                fullWidth
                sx={{ py: 1.5 }}
                color="secondary"
              >
                {t("downloadCSVTemplateButton", { ns: "importWizard" })}
              </Button>
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, pl: 2 }}
                color={"#000000CC"}
              >
                {t("downloadCSVTemplateHelper", { ns: "importWizard" })}
              </Typography>
            </Box>
            <Box>
              <AuturaDropdown
                name="tenant.data.savedImportMappings"
                label={t("tenant.data.savedImportMappings.label", {
                  ns: "entities",
                })}
                disabled={!appState.tenant?.data?.savedImportMappings}
                value={
                  appState.tenant?.data?.savedImportMappings?.find(
                    (mapping) => mapping.name === savedMapping?.name,
                  )?.name || t("")
                }
                setValue={handleSelectSavedMapping}
                options={[
                  ...(
                    appState.tenant?.data?.savedImportMappings || [
                      t("noSavedMappings", { ns: "importWizard" }),
                    ]
                  ).map((pt) => pt.name),
                ]}
                helperText={t("savedMappingsHelper", { ns: "importWizard" })}
              />
            </Box>
          </Stack>
          <Stack direction="column" spacing={4} flex={3}>
            <List disablePadding>
              {centerAlerts.map((props, index) => (
                <ImportAlert key={index} {...props} />
              ))}
            </List>
          </Stack>
          <Stack direction="column" spacing={0} flex={2}>
            {rightColumn}
          </Stack>
        </Stack>
      </ScrollableFormContainer>
    </Stack>
  );
};

export default UploadStep;
